import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Mail, Lock, Eye, EyeOff, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [step, setStep] = useState(token ? 'reset' : 'request'); // 'request' or 'reset'
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // Request form
  const [email, setEmail] = useState('');
  
  // Reset form
  const [newCode, setNewCode] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [showCode, setShowCode] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Veuillez entrer votre email');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/api/admin/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
        toast.success('Email envoyé ! Vérifiez vos spams si besoin.');
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!newCode || !confirmCode) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    if (newCode !== confirmCode) {
      toast.error('Les codes ne correspondent pas');
      return;
    }
    
    if (newCode.length < 8) {
      toast.error('Le code doit contenir au moins 8 caractères');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/api/admin/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          token: token,
          new_secret_code: newCode 
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
        toast.success('Code secret modifié !');
        setTimeout(() => navigate('/admin'), 3000);
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy flex flex-col">
      {/* Header */}
      <header className="py-6 px-6 border-b border-white/10">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin')}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <h1 className="font-heading text-xl font-bold text-white">
              L'ŒIL DU MARCHAND
            </h1>
          </div>
          <span className="text-gold font-medium">ESPACE ADMIN</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md bg-white/5 backdrop-blur border-0">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-gold" />
            </div>
            <CardTitle className="text-2xl font-bold text-white">
              {step === 'request' ? 'Mot de passe oublié' : 'Nouveau code secret'}
            </CardTitle>
            <p className="text-white/60 text-sm mt-2">
              {step === 'request' 
                ? 'Entrez votre email pour recevoir un lien de réinitialisation'
                : 'Définissez votre nouveau code secret admin'
              }
            </p>
          </CardHeader>
          <CardContent>
            {/* SUCCESS STATE */}
            {success && step === 'request' && (
              <div className="text-center py-6">
                <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                <h3 className="text-white font-semibold text-lg mb-2">Email envoyé !</h3>
                <p className="text-white/70 text-sm mb-4">
                  Si cet email est enregistré, vous recevrez un lien de réinitialisation.
                </p>
                <div className="p-3 bg-amber-500/20 border border-amber-500/30 rounded-lg mb-4">
                  <p className="text-amber-300 text-sm">
                    ⚠️ <strong>Vérifiez vos spams</strong> si vous ne recevez pas l'email dans les prochaines minutes.
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/admin')}
                  className="mt-2 bg-gold text-navy hover:bg-gold/90"
                >
                  Retour à la connexion
                </Button>
              </div>
            )}

            {success && step === 'reset' && (
              <div className="text-center py-6">
                <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                <h3 className="text-white font-semibold text-lg mb-2">Code modifié !</h3>
                <p className="text-white/70 text-sm">
                  Votre nouveau code secret a été enregistré. Vous allez être redirigé...
                </p>
              </div>
            )}

            {/* REQUEST FORM */}
            {!success && step === 'request' && (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div>
                  <Label className="text-white/80">Email admin</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="email"
                      placeholder="votre@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gold text-navy hover:bg-gold/90 font-semibold py-6"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    'Envoyer le lien'
                  )}
                </Button>
              </form>
            )}

            {/* RESET FORM */}
            {!success && step === 'reset' && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <Label className="text-white/80">Nouveau code secret</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type={showCode ? 'text' : 'password'}
                      placeholder="Minimum 8 caractères"
                      value={newCode}
                      onChange={(e) => setNewCode(e.target.value)}
                      className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCode(!showCode)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showCode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-white/80">Confirmer le code secret</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type={showCode ? 'text' : 'password'}
                      placeholder="Retapez le code"
                      value={confirmCode}
                      onChange={(e) => setConfirmCode(e.target.value)}
                      className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gold text-navy hover:bg-gold/90 font-semibold py-6"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Modification en cours...
                    </>
                  ) : (
                    'Modifier mon code secret'
                  )}
                </Button>
              </form>
            )}

            {/* Back to login */}
            {!success && (
              <div className="mt-6 text-center">
                <button
                  onClick={() => navigate('/admin')}
                  className="text-white/50 text-sm hover:text-white/70"
                >
                  ← Retour à la connexion
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
